// Natural Draft answers -> the Sales Burner Configurator's parameters.
//
// The configurator (Cody's, Azure DevOps "Sales Burner Configurator", vendored into
// public/burner/ by scripts/sync-burner-configurator.cjs) draws the burner from its own
// parameter set P. This file is the ONLY place that knows both vocabularies, so the live
// model beside the questions and the drawing + STEP attached to the item on Save are
// built from one translation and cannot disagree.
//
// The field pairs are Kevin's sheet (2026-10-13), "CD Field" -> "CPQ Field":
//   FT_DIA <- Flange ID          FT_FLANGE_OD <- Flange OD     FT_NECK_LENGTH <- Neck Height
//   FT_OFFSET_H/V <- Offset 1 & 2 Side + Distance              BODY_DIA <- Cell OD
//   BODY_HEIGHT <- Height        FT_BOLT_* <- Bolt Hole Quantity / Size / Centers
//   flats <- Hood Type, Flat Side Orientation 1/2, Flat Side Size
//   connections <- Up / Inlet Connection Size / Orientation (Main, Pilot, Aux)
//   hinges: access <- 6x6 Access + orientation, D-flange <- Hinge Size, hand <- Hinge Direction
//   box: Box_Base <- Height (the box is a cube), Manway_Diameter <- Manway ID,
//        cells <- Flame Cell Location 1-4, connections <- Panel / Origin / In / Up
// and the type table: Housing + Bolted/Clamped + Design -> Box / O / Bolted Hinge / QC Hinge
// in Standard, Air Control or EAS (+LN).
//
// TWO DIRECTIONS ARE ASSUMED, flagged "check with Cody" on the sheet itself:
//   - round offsets: Left = +H (the configurator's "left is +x, viewed from the hood end"),
//     Up = +V;
//   - box Origin: Left/Right read standing outside the panel (converted in the
//     configurator's export/embed.js, which owns its own frame).
//
// What the model cannot show is returned as `notes`, worded for the salesperson, so a
// drawing that differs from the order says where.
import type { AnswerState } from "../types";

export type BurnerType = "box" | "o" | "hinge" | "qchinge";
/** A box connection placed the way the CPQ places it; embed.js converts it. */
export type BoxPort = {
  id: string;
  panel: "xp" | "xn" | "zp" | "zn";
  from: "left" | "right";
  in: number;
  up: number;
  size: string;
};
export type BurnerConfig = {
  type: BurnerType;
  params: Record<string, string | number>;
  ports?: BoxPort[];
};
export type BurnerModel = {
  config: BurnerConfig;
  /** The CPQ's own model code (HOUSING_VALIDATION), e.g. "HCR-FAH42/24-20". */
  model: string;
  notes: string[];
};

/** The configurator's parameters in CPQ words, for "ordered X, drawn Y" when it changes one
 *  (a range, a style's lock, a value it cannot build). Connection keys are matched by suffix. */
export function modelParamLabel(k: string): string {
  const base = k.replace(/^o_/, "");
  const fixed: Record<string, string> = {
    Body_Dia: "Cell OD", Body_Height: "Height", Box_Base: "Housing size", FT_DIA: "Flange ID",
    FT_FLANGE_OD: "Flange OD", FT_NECK: "Neck height", FT_NECK_LENGTH: "Neck height",
    FT_OFFSET_H: "Offset left/right", FT_OFFSET_V: "Offset up/down", FT_OFFSET: "Offset left/right",
    ui_FT_offV: "Offset up/down", Manway_Diameter: "Manway ID", FT_BOLT_HOLES: "Bolt hole quantity",
    FT_BOLT_HOLE_DIA: "Bolt hole size", FT_BOLT_CIRCLE: "Bolt hole centers", Flat_Depth: "Flat side size",
    h_Access: "6x6 access", h_Cell_Housing_DIA: "Cell housing",
  };
  if (fixed[base]) return fixed[base];
  const m = base.match(/^(Main|Pilot|Option_(\d+)|Opt(\d+))_?(In|Up|Location|Angle|size)$/);
  if (m) {
    const who = m[1] === "Main" || m[1] === "Pilot" ? m[1] : `Aux ${m[2] ?? m[3]}`;
    const what = { In: "In", Up: "Up", Location: "Up", Angle: "Orientation", size: "Size" }[m[4]];
    return `${who} ${what}`;
  }
  return k;
}

/** The coupling sizes the configurator models (core/units.js NPS). */
const NPS: ReadonlyArray<readonly [string, number]> = [
  ["1/4", 0.25], ["3/8", 0.375], ["1/2", 0.5], ["3/4", 0.75],
  ["1", 1], ["1-1/4", 1.25], ["1-1/2", 1.5], ["2", 2],
];
/** Most connections the configurator registers beyond Main and Pilot (OPT_MAX). */
const MAX_OPTIONS = 6;

/** "25-1/4", "2-1/2\"", "1 1/4", "12.5", "24\"", 32 -> inches; null when not a size. */
export function inches(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const s = v.replace(/["″]/g, "").trim();
  const m = s.match(/^(\d+(?:\.\d+)?)?(?:[\s-]*(\d+)\/(\d+))?$/);
  if (!m || (!m[1] && !m[2])) return null;
  const whole = m[1] ? Number(m[1]) : 0;
  const frac = m[2] ? Number(m[2]) / Number(m[3]) : 0;
  const n = whole + frac;
  return Number.isFinite(n) ? n : null;
}

/** The option picked on a single-select question: its answer code, or null. */
function picked(a: AnswerState, q: string): string | null {
  const pre = q + "/";
  for (const k in a) if (k.startsWith(pre) && a[k] === true) return k.slice(pre.length);
  return null;
}
/** A free value ("Q/A" holding text or a number), trimmed; "" and "None" read as absent. */
function text(a: AnswerState, key: string): string | null {
  const v = a[key];
  if (v == null || v === false || v === true) return null;
  const s = String(v).trim();
  return s === "" || /^none$/i.test(s) ? null : s;
}
/** Decimal codes: "15_75" -> 15.75, "4_5" -> 4.5, "20" -> 20. */
function decimalCode(code: string | null): number | null {
  if (!code || !/^\d+(_\d+)?$/.test(code)) return null;
  return Number(code.replace("_", "."));
}
/** Fraction codes: "5_8" -> 5/8, "1_1_16" -> 1-1/16, "1" -> 1. */
function fractionCode(code: string | null): number | null {
  if (!code) return null;
  const p = code.split("_").map(Number);
  if (p.some((x) => !Number.isFinite(x))) return null;
  if (p.length === 1) return p[0];
  if (p.length === 2) return p[0] / p[1];
  if (p.length === 3) return p[0] + p[1] / p[2];
  return null;
}
/** A CPQ connection size onto the model's table: exact when it is there, else the nearest. */
function couplingSize(raw: string, label: string, notes: string[]): string {
  const want = inches(raw);
  const norm = raw.replace(/["″]/g, "").trim().replace(/\s+/, "-");
  const exact = NPS.find(([id]) => id === norm);
  if (exact) return exact[0];
  let best = NPS[0];
  if (want != null) for (const s of NPS) if (Math.abs(s[1] - want) < Math.abs(best[1] - want)) best = s;
  notes.push(`${label} ${raw}" is not in the model's coupling table; drawn as ${best[0]}".`);
  return best[0];
}
function portLabel(key: string): string {
  if (key === "MAIN_1") return "Main";
  if (key === "PILOT_1") return "Pilot";
  const m = key.match(/^AUX_(\d+)_1$/);
  return m ? `Aux ${m[1]}` : key;
}

/** The connections this configuration has, in schedule order: Main, Pilot, Aux 1..n. */
function connectionKeys(a: AnswerState): string[] {
  const aux = new Set<number>();
  for (const k in a) {
    const m = k.match(/^SIZE_TABLE\/AUX_(\d+)_1$/);
    if (m) aux.add(Number(m[1]));
  }
  return ["MAIN_1", "PILOT_1", ...[...aux].sort((x, y) => x - y).map((n) => `AUX_${n}_1`)]
    .filter((k) => text(a, `SIZE_TABLE/${k}`) != null);
}

function burnerType(a: AnswerState): BurnerType | null {
  const housing = text(a, "HOUSING/HOUSING");
  if (housing === "Box-Type") return "box";
  if (housing === "O-Type") return "o";
  if (housing === "Hinge Type") return picked(a, "MOUNTING") === "CLAMPED" ? "qchinge" : "hinge";
  return null;
}

/** Design -> the configurator's style. Only the hinges have styles beyond Standard. */
function applyDesign(type: BurnerType, design: string, p: BurnerConfig["params"], notes: string[]) {
  const hinge = type === "hinge" || type === "qchinge";
  if (hinge && design.startsWith("EAS")) {
    p.Unit_Style = "eas";
    p.h_EAS_LN = design.endsWith("LN") ? "on" : "off";
    if (design !== "EAS" && design !== "EASLN")
      notes.push(`Design ${design} is drawn as EAS${design.endsWith("LN") ? " LN" : ""}.`);
    return;
  }
  if (hinge && design.startsWith("AC")) {
    p.Unit_Style = "ac";
    if (design !== "AC") notes.push(`Design ${design} is drawn as Air Control.`);
    return;
  }
  p.Unit_Style = "standard";
  if (design && design !== "STD") notes.push(`Design ${design} is drawn as Standard.`);
}

/** Flange bolting: the same three numbers on both families, o_-prefixed on the round ones. */
function applyBolting(a: AnswerState, pre: "" | "o_", p: BurnerConfig["params"], notes: string[]) {
  const pattern = picked(a, "PATTERN_TYPE");
  const count = decimalCode(picked(a, "BOLT_HOLE_QUANTITY"));
  if (pattern === "PATTERN_BLANK" || !count) {
    p.ui_FT_Bolts = "off";
    return;
  }
  p.ui_FT_Bolts = "on";
  p[`${pre}FT_BOLT_HOLES`] = count;
  const hole = fractionCode(picked(a, "BOLT_HOLE_SIZE"));
  if (hole) p[`${pre}FT_BOLT_HOLE_DIA`] = hole;
  const bc = inches(text(a, "BOLT_HOLE_CENTERS/BOLT_HOLE_CENTERS"));
  // Auto puts the circle where the holes clear the neck; the order's own circle wins.
  p[pre ? "o_BC_Mode" : "BC_Mode"] = bc ? "custom" : "auto";
  if (bc) p[`${pre}FT_BOLT_CIRCLE`] = bc;
  if (pattern === "PATTERN_ONE_HOLE")
    notes.push("One-hole flange pattern: the model straddles the centre line (two-hole).");
}

/** Offset 1 and 2 as signed horizontal / vertical amounts (Left and Up positive). */
function offsets(a: AnswerState): { h: number; v: number } {
  let h = 0, v = 0;
  if (picked(a, "FLANGE_POSITION") !== "FLANGE_OFFSET") return { h, v };
  for (const n of [1, 2]) {
    const side = picked(a, `OFFSET_${n}_SIDE`);
    const dist = decimalCode(picked(a, `OFFSET_${n}_DISTANCE`)) ?? 0;
    if (side === "OFFSET_LEFT") h = dist;
    else if (side === "OFFSET_RIGHT") h = -dist;
    else if (side === "OFFSET_UP") v = dist;
    else if (side === "OFFSET_DOWN") v = -dist;
  }
  return { h, v };
}

function roundModel(a: AnswerState, type: BurnerType, p: BurnerConfig["params"], notes: string[]) {
  const body = decimalCode(picked(a, "CELL_OD"));
  if (body) p.o_Body_Dia = body;
  const height = inches(text(a, "DIMENSIONS/DIMENSIONS"));
  if (height) p.o_Body_Height = height;
  const ft = decimalCode(picked(a, "FLANGE_ID"));
  if (ft) p.o_FT_DIA = ft;
  const flg = inches(text(a, "FLANGE_OD/FLANGE_OD"));
  // No Flange OD answered: FT_DIA + 4, the rule every export follows.
  p.o_FT_FLANGE_OD = flg ?? (Number(p.o_FT_DIA ?? 18) + 4);
  const neck = picked(a, "NECK_HEIGHT");
  if (neck) p.o_FT_NECK_LENGTH = neck === "NECK_NONE" ? 0 : Number(neck.replace("NECK_", "")) || 0;
  const off = offsets(a);
  p.o_FT_OFFSET_H = off.h;
  p.o_FT_OFFSET_V = off.v;
  applyBolting(a, "o_", p, notes);

  // Flat-side hood: up to two flats, at the configurator's four angles, one depth for both.
  const hood = picked(a, "HOOD_TYPE");
  const flats = hood === "HOOD_FLATSIDE" ? 1 : hood === "HOOD_DUAL_FLATSIDE" ? 2 : 0;
  p.o_Flat_Count = String(flats);
  if (flats) {
    (["FLAT_SIDE_ORIENTATION", "FLAT_SIDE_ORIENTATION_2"] as const).slice(0, flats).forEach((q, i) => {
      const deg = Number((picked(a, q) ?? "").replace("FLAT_SIDE_", ""));
      if (!Number.isFinite(deg)) return;
      const snapped = (Math.round(deg / 90) * 90) % 360;
      if (snapped !== deg) notes.push(`Flat side ${i + 1} at ${deg}° is drawn at ${snapped}° (the model has 0/90/180/270).`);
      p[`o_Flat${i + 1}_Angle`] = String(snapped);
    });
    const depth = fractionCode(picked(a, "FLATSIDE") === "1_12" ? "1_1_2" : picked(a, "FLATSIDE"));
    if (depth) p.o_Flat_Depth = String(depth);
  } else if (hood && hood.startsWith("HOOD_PERF")) {
    notes.push("Perforated hood sections are not drawn.");
  }

  if (type === "hinge" || type === "qchinge") {
    if (body) p.h_Cell_Housing_DIA = body;
    const hand = picked(a, "HINGE_DIRECTION");
    if (hand === "LEFT" || hand === "RIGHT") p.h_Hinge_Side = hand.toLowerCase();
    const df = picked(a, "HINGE_SIZE");
    if (df === "2" || df === "3") p.h_DFlange = df;
    if (picked(a, "6X6_ACCESS") === "6X6_ACCESS_YES") {
      const at = picked(a, "ACCESS_ORIENTATION");
      p.h_Access = at === "90" || at === "270" ? at : "auto";
      if (at && at !== "90" && at !== "270" && at !== "NONE")
        notes.push(`6x6 access at ${at}° is drawn opposite the firetube offset (the model has 90/270).`);
    } else p.h_Access = "off";
  }

  // Connections on the shell: angle round the clock, Location back from the front face.
  let opt = 0;
  for (const key of connectionKeys(a)) {
    const label = portLabel(key);
    const angRaw = text(a, `ORIENTATION_TABLE/${key}`);
    if (angRaw === "Firetube") {
      notes.push(`${label} on the firetube face is not drawn.`);
      continue;
    }
    const ang = inches(angRaw);
    const loc = inches(text(a, `UP/${key}`));
    if (ang == null || loc == null) continue;
    const pk = key === "MAIN_1" ? "o_Main" : key === "PILOT_1" ? "o_Pilot" : null;
    let k = pk;
    if (!k) {
      if (opt >= MAX_OPTIONS) {
        notes.push(`${label} is not drawn (the model has room for ${MAX_OPTIONS} aux connections).`);
        continue;
      }
      k = `o_Opt${++opt}`;
    }
    p[`${k}_Angle`] = ang;
    p[`${k}_Location`] = loc;
    p[`${k}_size`] = couplingSize(String(a[`SIZE_TABLE/${key}`]), label, notes);

    // The model places a shell connection by angle and Up only. A From Center IN (a sideways
    // shift) has no counterpart, and whether it follows an offset firetube is fixed: Main
    // and Pilot are piped to the mixer so they do, an Aux stays on the housing centre line.
    const inn = inches(text(a, `IN/${key}`));
    if (a["FROM_CENTER_MODE/ON"] === true && inn) {
      const side = text(a, `FROM_CENTER/${key}`);
      notes.push(`${label} ${inn}"${side ? ` ${side.toLowerCase()} of` : " from"} centre is drawn on the centre line.`);
    }
    if (off.h || off.v) {
      const match = text(a, `MATCH_OFFSET_POSITION/${key}`);
      if (pk && match === "Standard") notes.push(`${label} is drawn following the firetube offset.`);
      if (!pk && match === "Match Offset") notes.push(`${label} is drawn on the housing centre line.`);
    }
  }
  p.o_optCount = opt;
}

const BOX_PANEL: Record<string, BoxPort["panel"]> = {
  FireTube: "xn", Manway: "xp", Left: "zp", Right: "zn",
};

function boxModel(a: AnswerState, p: BurnerConfig["params"], notes: string[]): BoxPort[] {
  // Housing size is the model code's first number (BT-FAH34/34-20); the model's box is a
  // cube, so a code whose height differs (an actuated SB44/48) is drawn at its base.
  const code = (text(a, "HOUSING_VALIDATION/HOUSING_VALIDATION") ?? "").match(/(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)-/);
  const height = inches(text(a, "DIMENSIONS/DIMENSIONS"));
  const size = code ? Number(code[1]) : height;
  if (size) p.Box_Base = size;
  if (size && height && height !== size) notes.push(`The box is drawn ${size}" square; its height is ${height}".`);
  const ft = decimalCode(picked(a, "FLANGE_ID"));
  if (ft) p.FT_DIA = ft;
  const flg = inches(text(a, "FLANGE_OD/FLANGE_OD"));
  p.FT_FLANGE_OD = flg ?? (Number(p.FT_DIA ?? 30) + 4);
  const neck = picked(a, "NECK_HEIGHT");
  if (neck) p.FT_NECK = neck === "NECK_NONE" ? 0 : Number(neck.replace("NECK_", "")) || 0;
  const mw = inches(text(a, "MANWAYID/MANWAYID"));
  if (mw) p.Manway_Diameter = mw;
  const off = offsets(a);
  p.FT_OFFSET = Math.abs(off.h);
  p.ui_FT_dirH = off.h > 0 ? "left" : off.h < 0 ? "right" : "center";
  p.ui_FT_offV = Math.abs(off.v);
  p.ui_FT_dirV = off.v > 0 ? "up" : off.v < 0 ? "down" : "center";
  applyBolting(a, "", p, notes);

  // Flame cells: every panel set explicitly, so the type's default top + bottom never leaks.
  const cells = new Set<string>();
  const qty = Number(text(a, "CELL_QUANTITYV/CELL_QUANTITYV") ?? 0) || 0;
  for (let n = 1; n <= Math.min(qty, 4); n++) {
    const at = picked(a, `CELL_${n}LOCATION`);
    if (at === "FRONT") notes.push(`Flame cell ${n} on the manway is not drawn.`);
    else if (at) cells.add(at.toLowerCase());
  }
  for (const c of ["top", "bottom", "left", "right"]) p[`ui_cell_${c}`] = cells.has(c) ? "on" : "off";

  // Connections: Panel / Origin / In / Up, converted to the model's own datum in embed.js.
  const ports: BoxPort[] = [];
  let opt = 0;
  for (const key of connectionKeys(a)) {
    const label = portLabel(key);
    const panelRaw = text(a, `PANEL/${key}`);
    const panel = panelRaw ? BOX_PANEL[panelRaw] : undefined;
    const inn = inches(text(a, `IN/${key}`));
    const up = inches(text(a, `UP/${key}`));
    if (!panel) {
      if (panelRaw) notes.push(`${label} on the ${panelRaw.toLowerCase()} panel is not drawn.`);
      continue;
    }
    if (inn == null || up == null) continue;
    let id = key === "MAIN_1" ? "Main" : key === "PILOT_1" ? "Pilot" : null;
    if (!id) {
      if (opt >= MAX_OPTIONS) {
        notes.push(`${label} is not drawn (the model has room for ${MAX_OPTIONS} aux connections).`);
        continue;
      }
      id = `Option_${++opt}`;
    }
    const origin = text(a, `ORIGIN/${key}`) ?? "Left-Bottom";
    ports.push({
      id, panel, in: inn, up,
      from: origin.startsWith("Right") ? "right" : "left",
      size: couplingSize(String(a[`SIZE_TABLE/${key}`]), label, notes),
    });
  }
  p.ui_optCount = opt;
  p.ui_main2 = "off";
  p.ui_pilot2 = "off";
  return ports;
}

/** The burner this configuration describes, or null until a housing is chosen. */
export function ndbBurnerModel(a: AnswerState): BurnerModel | null {
  const type = burnerType(a);
  if (!type) return null;
  const notes: string[] = [];
  const params: BurnerConfig["params"] = {};
  applyDesign(type, text(a, "DESIGN/DESIGN") ?? "STD", params, notes);
  // The part name's product letters (Cody's v1.1.0 Unit_Product: FAH / SB / MB), from the
  // burner count the way the CPQ names the model: "0 - FAH" is a housing with no main burner.
  const qty = picked(a, "BURNER_QTY");
  params.Unit_Product = qty === "MAIN_BURNER_0" ? "FAH" : qty === "MAIN_BURNER_1" ? "SB" : qty ? "MB" : "FAH";
  const config: BurnerConfig = { type, params };
  if (type === "box") config.ports = boxModel(a, params, notes);
  else roundModel(a, type, params, notes);
  return { config, model: text(a, "HOUSING_VALIDATION/HOUSING_VALIDATION") ?? "", notes };
}
